// Shared publisher form used by both /self-service/onboard (create) and
// /self-service/admin/publishers/:slug (edit). Owns its own state; the parent
// page hands in initial values and a submit handler that hits the right edge
// function.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Building2, Palette, DollarSign, FileText, MapPin, MonitorSmartphone, Tv,
  CheckCircle2, XCircle, Loader2, Plus, Trash2, Film, Sparkles,
} from 'lucide-react';
import { apiClient } from '../../../lib/apiClient';
import { DMA_REGIONS } from '../../../data/dmaList';
import type { PublisherConfig } from '../../../types/selfService';
import { STREAMING_DEVICES } from '../../../types/publisherIntake';
import { useAuth } from '../auth/useAuth';
import BackgroundVideoBuilder from './BackgroundVideoBuilder';
import { buildStationBackgroundVideo } from './backgroundVideoApi';
import { extractThumbnails } from './clipThumbnails';
import { toRenderClips, type TimelineClip } from './clipTimeline';
import { probeIntakeClipDurations } from './publisherIntakeToConfig';
import { inferStateFromDmaName } from './inferStateFromDmaName';
import StateSelector from '../shared/StateSelector';

import { Section } from './fields/FormSection';
import IdentityFields from './fields/IdentityFields';
import { redirectTopPaddingClickToCaretEnd } from './fields/caretAtEnd';
import BrandingFields from './fields/BrandingFields';
import MetroCombobox from './fields/MetroCombobox';
import InternalNameField from './fields/InternalNameField';
import { inputClass, readonlyInputClass, labelClass } from './fields/publisherFieldStyles';
import type { MarketRow, IdentityValues, BrandingValues } from './fields/types';
import {
  appendBlankMarketRow, applyStatesToAll, flatStatesOf, removeMarketRow, selectDmaForRow,
  toMarketsPayload, updateMarketRow,
} from './fields/marketRows';

export interface FormSectionProgress {
  id: string;
  title: string;
  done: boolean;
  optional?: boolean;
}

export interface PublisherFormPayload {
  slug: string;
  name: string;
  displayName: string | null;
  // Required when a super_admin creates a channel (their JWT carries no
  // organization_id). Org_admins can omit it — the API derives org from
  // their JWT and ignores any body override.
  organizationId?: string | null;
  logoUrl: string;
  logoInvert: boolean;
  logoScale: number;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  fontFamily: string | null;
  fontUrl: string | null;
  coverage: 'regional' | 'national';
  markets: { dmaCode: string; dmaName: string; channels: string[]; coveredStates: string[] }[];
  // CTV-431: one Run-of-Network product per station, server-managed. The form
  // only sends the admin-controlled CPM and the included Publica sub-channel
  // names; name + description are hardcoded in the backend.
  ronCpm: number;
  ronIncludedSubChannels: string[];
  revSharePercent: number;
  minBudget: number;
  minCampaignDays: number;
  supportEmail: string;
  adopsEmail: string;
  adopsPhone: string;
  defaultCommissionPercent: number | null;
  termsUrl: string;
  // CTV-752: shows/hides the publisher admin's Materials tab.
  materialsEnabled: boolean;
  // CTV-803: devices the station streams on. Admin-owned since CTV-855.
  devices: string[];
}

interface Props {
  mode: 'create' | 'edit';
  initial: PublisherConfig | null;
  onSubmit: (payload: PublisherFormPayload) => Promise<{ slug: string }>;
  submitLabel?: string;
  /**
   * Seeds the background-video editor. Used when hydrating from a publisher's
   * intake submission so the clips they uploaded render on create.
   */
  initialBackgroundClips?: TimelineClip[];
  /**
   * When set, marks the form as pre-filled from this publisher's submission and
   * flags which sections are still internal-only. Undefined = today's behavior.
   */
  sourceLabel?: string | null;
  /**
   * Live per-section progress for a page-owned tracker rail. Reported from an
   * effect whenever completion flips; sections carry the anchor ids rendered
   * around each <Section>.
   */
  onSectionProgress?: (sections: FormSectionProgress[]) => void;
  /**
   * The owning organization is already decided (it came from the publisher's
   * intake link), so render it read-only instead of asking again.
   */
  organizationLocked?: boolean;
}

const PublisherForm: React.FC<Props> = ({
  mode,
  initial,
  onSubmit,
  submitLabel,
  initialBackgroundClips,
  sourceLabel,
  organizationLocked = false,
  onSectionProgress,
}) => {
  const { user } = useAuth();
  const isSuperAdminCreating = mode === 'create' && user?.rawRole === 'super_admin';

  // Super_admins onboard channels across organizations and must pick the
  // owning org (their JWT carries no organization_id). Org_admins onboard
  // implicitly into their own org — no picker needed.
  const [organizationId, setOrganizationId] = useState<string>(initial?.organizationId ?? '');
  const [organizations, setOrganizations] = useState<{ id: string; name: string }[]>([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);

  useEffect(() => {
    if (!isSuperAdminCreating) return;
    let cancelled = false;
    setOrganizationsLoading(true);
    (async () => {
      try {
        const rows = await apiClient.get<{ id: string; name: string }[]>('/admin/organizations');
        if (!cancelled) setOrganizations(rows ?? []);
      } catch (e) {
        console.warn('[PublisherForm] failed to load organizations:', e);
      } finally {
        if (!cancelled) setOrganizationsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isSuperAdminCreating]);

  const [slug, setSlug] = useState(initial?.slug ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [displayName, setDisplayName] = useState(initial?.displayName ?? '');
  const [website, setWebsite] = useState('');

  const [logoUrl, setLogoUrl] = useState(initial?.logoUrl ?? '');
  const [logoNotTransparent, setLogoNotTransparent] = useState(false);
  const [logoInvert, setLogoInvert] = useState(initial?.logoInvert ?? false);
  const [logoScale, setLogoScale] = useState<number>(initial?.logoScale ?? 1);
  // CTV-610: landing background-video composition. Non-empty only when the
  // admin uploads clips this session; the render is kicked on submit.
  const [backgroundClips, setBackgroundClips] = useState<TimelineClip[]>(
    initialBackgroundClips ?? [],
  );

  // Clips hydrated from a publisher's submission carry no duration (their
  // uploader doesn't trim), so read it off each signed URL and lay them out.
  // A zero-length clip can neither be previewed nor rendered.
  useEffect(() => {
    if (!initialBackgroundClips?.length) return;
    let cancelled = false;
    void probeIntakeClipDurations(initialBackgroundClips).then((probed) => {
      if (cancelled || !probed) return;
      // Merge, don't replace: a clip whose filmstrip extraction (below) beat
      // this probe already carries thumbnails the probed copy lacks.
      setBackgroundClips((prev) =>
        probed.map((p) => {
          const existing = prev.find((x) => x.id === p.id);
          return existing?.thumbnails?.length ? { ...p, thumbnails: existing.thumbnails } : p;
        }),
      );
    });
    // Filmstrips too: hydrated clips arrive without thumbnails, patch each
    // clip (by id) as its frames extract, same as the upload path (CTV-745).
    for (const c of initialBackgroundClips) {
      if (!c.previewUrl) continue;
      void extractThumbnails(c.previewUrl).then((thumbnails) => {
        if (cancelled || thumbnails.length === 0) return;
        setBackgroundClips((prev) =>
          prev.map((p) => (p.id === c.id ? { ...p, thumbnails } : p)),
        );
      });
    }
    return () => { cancelled = true; };
  }, [initialBackgroundClips]);
  const [buildingVideo, setBuildingVideo] = useState(false);
  const [primaryColor, setPrimaryColor] = useState(initial?.primaryColor ?? '');
  const [secondaryColor, setSecondaryColor] = useState(initial?.secondaryColor ?? '');
  const [accentColor, setAccentColor] = useState(initial?.accentColor ?? '');
  const [fontFamily, setFontFamily] = useState(initial?.fontFamily ?? '');
  const [fontUrl, setFontUrl] = useState(initial?.fontUrl ?? '');

  const [coverage, setCoverage] = useState<'regional' | 'national'>(initial?.coverage ?? 'regional');
  const [supportEmail, setSupportEmail] = useState(initial?.supportEmail ?? '');
  const [adopsEmail, setAdopsEmail] = useState(initial?.adopsEmail ?? '');
  const [adopsPhone, setAdopsPhone] = useState(initial?.adopsPhone ?? '');
  const [commissionPercent, setCommissionPercent] = useState(
    initial?.defaultCommissionPercent != null ? String(initial.defaultCommissionPercent) : '',
  );
  // Checked from the template's toggle (may be enabled with no %) or, on an
  // existing station, from the % being configured at all.
  const [showCommission, setShowCommission] = useState(
    initial?.teamCommissionEnabled ?? initial?.defaultCommissionPercent != null,
  );
  const [termsUrl, setTermsUrl] = useState(initial?.termsUrl ?? '#');
  // CTV-752: Materials-tab switch. New stations start with the tab on.
  const [materialsEnabled, setMaterialsEnabled] = useState(initial?.materialsEnabled ?? true);
  // CTV-803: devices the station streams on. A Set for the checkbox grid,
  // serialized back to STREAMING_DEVICES order on submit.
  const [devices, setDevices] = useState<Set<string>>(
    () => new Set(initial?.devices ?? []),
  );
  // Two-decimal precision: negotiated rates like 15.5% must round-trip
  // load → edit → submit without truncation (× 10000 / 100, not × 1000 / 10).
  const [revSharePercent, setRevSharePercent] = useState(
    // New publishers default to the 20% product rev share (matches the
    // API/DB defaults).
    // Create-mode default: 25% (house standard as of CTV-680 form v2.1).
    initial ? String(Math.round(initial.revSharePercent * 10000) / 100) : '25',
  );
  const [minBudget, setMinBudget] = useState(initial ? String(initial.minBudget) : '250');
  const [minDays, setMinDays] = useState(initial ? String(initial.minCampaignDays) : '7');

  const [markets, setMarkets] = useState<MarketRow[]>(
    initial?.markets.map((m) => ({
      dmaCode: m.dmaCode,
      dmaName: m.dmaName,
      channels: m.channels.join(', '),
      coveredStates: m.coveredStates ?? [],
    })) ?? [],
  );

  // --- CTV-431: Run-of-Network product state -----------------------------
  // CPM is the only commercial knob; sub-channels are the canonical Publica
  // list (refetched per save), with an admin checkbox-driven inclusion set.
  const initialRon = initial?.products?.[0];
  const [ronCpm, setRonCpm] = useState<string>(
    initialRon && initialRon.cpm != null ? String(initialRon.cpm) : '',
  );
  const [availableSubChannels, setAvailableSubChannels] = useState<string[]>(
    initialRon?.subChannels ?? [],
  );
  const [includedSubChannels, setIncludedSubChannels] = useState<Set<string>>(
    () => new Set(initialRon?.subChannels ?? []),
  );
  const [subChannelsLoading, setSubChannelsLoading] = useState(false);
  const [subChannelsWarning, setSubChannelsWarning] = useState<string | null>(null);
  // CTV-745: the checkbox-defaulting rule needs "what was listed before this
  // fetch" (a ref, so the async handler reads the latest committed list, not
  // its closure) and whether any live fetch has completed yet.
  const availableSubChannelsRef = useRef(availableSubChannels);
  availableSubChannelsRef.current = availableSubChannels;
  const hasFetchedSubChannelsRef = useRef(false);

  const [extracting, setExtracting] = useState(false);
  const [extractingLogo, setExtractingLogo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // CTV-745: the org's full Publica channel catalog, feeding the internal-name
  // search in the Products card. Separate from the prefix-matched fetch below:
  // this answers "what channels exist", that answers "what will save match".
  // Best-effort: on failure the search just offers no suggestions, and the
  // prefix fetch below still surfaces its own load errors.
  const [orgChannels, setOrgChannels] = useState<string[]>([]);
  const [orgChannelsLoading, setOrgChannelsLoading] = useState(false);
  useEffect(() => {
    if (!organizationId) {
      setOrgChannels([]);
      return;
    }
    let cancelled = false;
    setOrgChannelsLoading(true);
    apiClient
      .get<{ channels: string[] }>(`/admin/organizations/${organizationId}/publica-channels`)
      .then((res) => {
        if (!cancelled) setOrgChannels(res?.channels ?? []);
      })
      .catch(() => {
        if (!cancelled) setOrgChannels([]);
      })
      .finally(() => {
        if (!cancelled) setOrgChannelsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  // Refetch the canonical Publica sub-channel list whenever the org or
  // internal name changes. Mirrors what the backend will do on save, so
  // admins see the same set of checkboxes the server will validate against.
  // Edit mode: organizationId is preloaded from the PublisherConfig, so this
  // also fires on initial mount with the existing station's name. Create
  // mode (super_admin): fires once both fields are populated.
  useEffect(() => {
    const trimmedName = name.trim();
    if (!organizationId || !trimmedName) {
      // Don't wipe the checkbox list while the admin is mid-typing — only
      // clear it when both inputs are missing (which is the initial-create
      // state). On edit, organizationId is always set, so this branch never
      // resets the loaded list.
      if (!organizationId && !trimmedName) {
        setAvailableSubChannels([]);
        setIncludedSubChannels(new Set());
        setSubChannelsWarning(null);
      }
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setSubChannelsLoading(true);
      try {
        const result = await apiClient.get<{ channels: string[] }>(
          `/admin/organizations/${organizationId}/publica-channels?stationName=${encodeURIComponent(trimmedName)}`,
        );
        if (cancelled) return;
        const channels = result?.channels ?? [];
        // Snapshot BEFORE overwriting: "was this channel already listed"
        // drives the checkbox defaulting below.
        const prevListed = new Set(availableSubChannelsRef.current);
        const firstFetch = !hasFetchedSubChannelsRef.current;
        hasFetchedSubChannelsRef.current = true;
        setAvailableSubChannels(channels);
        if (channels.length === 0) {
          setIncludedSubChannels(new Set());
          setSubChannelsWarning(
            `No Publica channels matched "${trimmedName}". Save will be blocked until inventory exists.`,
          );
        } else {
          setSubChannelsWarning(null);
          // Newly-detected sub-channels default to included; channels that
          // stayed listed keep their checkbox state so refetches don't undo
          // the admin's deselections. "Newly detected" compares against the
          // last LISTED set, not the checked set: checking against the
          // checked set made a fully-renamed match set uncheck everything on
          // one keystroke and re-check it on the next (CTV-745). The first
          // fetch instead defers to the hydrated product, so a saved
          // station's deliberately-excluded sub-channels stay unchecked.
          setIncludedSubChannels((prev) => {
            const next = new Set<string>();
            for (const c of channels) {
              if (firstFetch ? prev.size === 0 || prev.has(c) : !prevListed.has(c) || prev.has(c)) {
                next.add(c);
              }
            }
            return next;
          });
        }
      } catch (e) {
        if (cancelled) return;
        setSubChannelsWarning(
          `Could not load Publica channels: ${e instanceof Error ? e.message : 'unknown error'}`,
        );
      } finally {
        if (!cancelled) setSubChannelsLoading(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [organizationId, name]);

  // When initial arrives async (edit mode loads after mount), hydrate state.
  useEffect(() => {
    if (!initial) return;
    setSlug(initial.slug);
    setName(initial.name);
    setDisplayName(initial.displayName ?? '');
    setLogoUrl(initial.logoUrl);
    setLogoInvert(initial.logoInvert ?? false);
    setLogoScale(initial.logoScale ?? 1);
    setPrimaryColor(initial.primaryColor);
    setSecondaryColor(initial.secondaryColor);
    setAccentColor(initial.accentColor);
    setFontFamily(initial.fontFamily);
    setFontUrl(initial.fontUrl ?? '');
    setCoverage(initial.coverage ?? 'regional');
    setSupportEmail(initial.supportEmail ?? '');
    setAdopsEmail(initial.adopsEmail ?? '');
    setAdopsPhone(initial.adopsPhone ?? '');
    setCommissionPercent(
      initial.defaultCommissionPercent != null ? String(initial.defaultCommissionPercent) : '',
    );
    setShowCommission(
      initial.teamCommissionEnabled ?? initial.defaultCommissionPercent != null,
    );
    setTermsUrl(initial.termsUrl);
    setMaterialsEnabled(initial.materialsEnabled ?? true);
    setDevices(new Set(initial.devices ?? []));
    setRevSharePercent(String(Math.round(initial.revSharePercent * 10000) / 100));
    setMinBudget(String(initial.minBudget));
    setMinDays(String(initial.minCampaignDays));
    setMarkets(initial.markets.map((m) => ({
      dmaCode: m.dmaCode,
      dmaName: m.dmaName,
      channels: m.channels.join(', '),
      coveredStates: m.coveredStates ?? [],
    })));
    const ron = initial.products?.[0];
    if (ron) {
      setRonCpm(String(ron.cpm ?? ''));
      setAvailableSubChannels((prev) => (prev.length > 0 ? prev : ron.subChannels));
      setIncludedSubChannels((prev) => (prev.size > 0 ? prev : new Set(ron.subChannels)));
    }
  }, [initial]);

  const toggleSubChannel = (channel: string) => {
    setIncludedSubChannels((prev) => {
      const next = new Set(prev);
      if (next.has(channel)) next.delete(channel);
      else next.add(channel);
      return next;
    });
  };

  const allChecked = useMemo(
    () =>
      availableSubChannels.length > 0 &&
      availableSubChannels.every((c) => includedSubChannels.has(c)),
    [availableSubChannels, includedSubChannels],
  );

  const handleExtract = async () => {
    if (!website.trim()) { setError('Enter a website URL first'); return; }
    setExtracting(true);
    setError(null);
    try {
      const result = await apiClient.post<{
        logoUrl: string | null;
        logoLikelyTransparent: boolean | null;
        siteTitle: string | null;
        primaryColor: string | null;
        secondaryColor: string | null;
        accentColor: string | null;
        fontFamily: string | null;
        fontUrl: string | null;
        bodyText: string | null;
      }>('/stations/extract-branding', { url: website.trim() });
      if (result.logoUrl) setLogoUrl(result.logoUrl);
      setLogoNotTransparent(result.logoUrl != null && result.logoLikelyTransparent === false);
      if (result.primaryColor) setPrimaryColor(result.primaryColor);
      if (result.secondaryColor) setSecondaryColor(result.secondaryColor);
      if (result.accentColor) setAccentColor(result.accentColor);
      if (result.fontFamily) setFontFamily(result.fontFamily);
      if (result.fontUrl) setFontUrl(result.fontUrl);
      if (result.siteTitle && !displayName) setDisplayName(result.siteTitle);

      let inferredDma: typeof DMA_REGIONS[number] | null = null;
      if (result.bodyText && markets.length === 0) {
        const haystack = ' ' + result.bodyText.toLowerCase() + ' ';
        const scores = DMA_REGIONS.map((d) => {
          const keywords = d.name.split(/[,\-/()]+/).map((k) => k.trim()).filter((k) => k.length >= 4);
          let score = 0;
          for (const kw of keywords) {
            const re = new RegExp(`\\b${kw.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g');
            const matches = haystack.match(re);
            if (matches) score += matches.length;
          }
          return { dma: d, score };
        }).filter((s) => s.score > 0).sort((a, b) => b.score - a.score);
        if (scores.length > 0 && scores[0].score >= 2) {
          inferredDma = scores[0].dma;
          const autoState = inferStateFromDmaName(inferredDma.name);
          setMarkets([{
            dmaCode: inferredDma.code,
            dmaName: inferredDma.name,
            channels: '',
            coveredStates: autoState ? [autoState] : [],
          }]);
        }
      }

      const anyHit = result.logoUrl || result.primaryColor || result.fontFamily || inferredDma;
      if (!anyHit) setError('Could not auto-detect branding. Fill in fields manually.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Extraction failed');
    } finally {
      setExtracting(false);
    }
  };

  // Re-pull just the logo from the website, leaving colors/font/markets/name
  // untouched — for fixing a bad logo without re-running full branding extract.
  const handleExtractLogo = async () => {
    if (!website.trim()) { setError('Enter a website URL first'); return; }
    setExtractingLogo(true);
    setError(null);
    setLogoNotTransparent(false);
    try {
      const result = await apiClient.post<{
        logoUrl: string | null;
        logoLikelyTransparent: boolean | null;
      }>('/stations/extract-branding', { url: website.trim() });
      if (result.logoUrl) {
        setLogoUrl(result.logoUrl);
        setLogoNotTransparent(result.logoLikelyTransparent === false);
      } else {
        setError('No logo found at that website.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Logo extraction failed');
    } finally {
      setExtractingLogo(false);
    }
  };

  // Live per-section completion for the onboard page's tracker rail.
  // Presence-of-required-fields, deliberately looser than submit validation:
  // the rail answers "have I been here yet", the API answers "is it valid".
  const sectionProgress: FormSectionProgress[] = [
    {
      id: 'pf-sec-identity',
      title: 'Identity',
      // CTV-745: the internal name moved to the Products card; identity is
      // the slug plus (for super_admin creates) the owning org.
      done:
        slug.trim().length >= 2
        && (!isSuperAdminCreating || organizationLocked || Boolean(organizationId)),
    },
    {
      id: 'pf-sec-branding',
      title: 'Branding',
      done: Boolean(logoUrl.trim()) && Boolean(primaryColor.trim()),
    },
    {
      id: 'pf-sec-video',
      title: 'Landing background video',
      done: backgroundClips.length > 0,
      optional: true,
    },
    {
      id: 'pf-sec-commercial',
      title: 'Commercial',
      // termsUrl still round-trips on save but has no input since CTV-745.
      done:
        Boolean(revSharePercent.trim())
        && Boolean(minBudget.trim())
        && Boolean(minDays.trim()),
    },
    {
      id: 'pf-sec-devices',
      title: 'Devices',
      // Optional here (unlike the intake): stations that predate the question
      // have no answer, and the server doesn't require one.
      done: devices.size > 0,
      optional: true,
    },
    {
      id: 'pf-sec-materials',
      title: 'Materials',
      // A checkbox always holds an answer; the card can't be "incomplete".
      done: true,
      optional: true,
    },
    {
      id: 'pf-sec-markets',
      title: 'Metros',
      done: markets.length > 0 && markets.some((m) => m.dmaCode),
    },
    {
      id: 'pf-sec-ron',
      title: 'Products',
      done: name.trim().length > 0 && Boolean(ronCpm.trim()) && includedSubChannels.size > 0,
    },
  ];
  const progressKey = sectionProgress
    .map((sec) => `${sec.id}:${sec.done ? 1 : 0}`)
    .join('|');
  const progressRef = React.useRef(sectionProgress);
  progressRef.current = sectionProgress;
  // progressKey is the real dependency: re-report only when a section flips,
  // reading the fresh array through the ref.
  useEffect(() => {
    onSectionProgress?.(progressRef.current);
  }, [progressKey, onSectionProgress]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setSubmitting(true);
    try {
      if (isSuperAdminCreating && !organizationId) {
        setError('Pick an organization to onboard this channel under.');
        setSubmitting(false);
        return;
      }
      const payload: PublisherFormPayload = {
        slug: slug.trim().toLowerCase(),
        name: name.trim(),
        displayName: displayName.trim() || null,
        organizationId: isSuperAdminCreating ? organizationId : null,
        logoUrl: logoUrl.trim(),
        logoInvert,
        logoScale,
        primaryColor, secondaryColor, accentColor,
        // Empty string is rejected by the API's validator (requireString min 1).
        // Coerce blanks to null so the field is treated as "not set" — branding
        // extraction sometimes misses the font and shouldn't block onboarding.
        fontFamily: fontFamily.trim() || null,
        fontUrl: fontUrl.trim() || null,
        coverage,
        markets: toMarketsPayload(markets),
        ronCpm: Number(ronCpm) || 0,
        ronIncludedSubChannels: Array.from(includedSubChannels),
        revSharePercent: Number(revSharePercent) / 100,
        minBudget: Number(minBudget),
        minCampaignDays: Number(minDays),
        supportEmail: supportEmail.trim(),
        adopsEmail: adopsEmail.trim(),
        adopsPhone: adopsPhone.trim(),
        // Mirrors the intake: unchecked means not configured, whatever the
        // input holds.
        defaultCommissionPercent:
          showCommission && commissionPercent.trim() ? Number(commissionPercent) : null,
        termsUrl: termsUrl.trim() || '#',
        materialsEnabled,
        devices: STREAMING_DEVICES.map((d) => d.value).filter((v) => devices.has(v)),
      };
      const { slug: resultSlug } = await onSubmit(payload);

      // CTV-610: on channel CREATE, if the admin composed a background video,
      // render it now that the station exists (onboarding has no slug for the
      // in-editor "Save & publish" button, which handles the edit case). A build
      // failure doesn't undo the save — surface it as a warning so they can retry.
      let videoNote = '';
      if (mode === 'create' && backgroundClips.length > 0) {
        setBuildingVideo(true);
        try {
          await buildStationBackgroundVideo(resultSlug, toRenderClips(backgroundClips));
          setBackgroundClips([]);
          videoNote = ' Background video rendered.';
        } catch (e) {
          videoNote = ` But the background video failed to render: ${
            e instanceof Error ? e.message : 'unknown error'
          }. You can retry from Channel settings.`;
        } finally {
          setBuildingVideo(false);
        }
      }

      setSuccessMessage(
        (mode === 'create' ? `Publisher "${resultSlug}" created.` : `Saved.`) + videoNote,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const updateMarket = (i: number, patch: Partial<MarketRow>) =>
    setMarkets((arr) => updateMarketRow(arr, i, patch));

  // Every market carries the same list; collapse it back for the single picker.
  const coveredStates = flatStatesOf(markets);

  // Adapters between this component's flat useState fields and the grouped
  // value objects the shared field components take.
  const identityValues: IdentityValues = { slug, name, displayName, website };
  // ==========================================================================
  // Publisher-answer guard (template mode only). Editing a value the
  // publisher specifically entered on their onboarding form pops a confirm,
  // once per field; internal-only fields (rev share, terms, markets, RON
  // sub-channels...) are the admin's to change freely. The commission toggle
  // is guarded in BOTH directions: unchecked was also their explicit answer.
  // ==========================================================================
  const guardActive = mode === 'create' && Boolean(sourceLabel);
  const publisherProvided = React.useMemo(() => {
    const set = new Set<string>();
    if (!guardActive || !initial) return set;
    if (initial.slug) set.add('slug');
    // CTV-745: the intake's name question seeds displayName; the internal
    // name is staff-owned and never publisher-provided.
    if (initial.displayName) set.add('displayName');
    if (initial.logoUrl) set.add('logoUrl');
    if (initial.primaryColor) set.add('primaryColor');
    if (initial.accentColor) set.add('accentColor');
    if (initial.products?.[0]?.cpm) set.add('ronCpm');
    if (initial.adopsEmail) set.add('adopsEmail');
    if (initial.adopsPhone) set.add('adopsPhone');
    // Devices are never guarded: the intake stopped asking in CTV-855, so
    // the admin's pick is the only answer.
    // The toggle question was asked either way; a blank % under an enabled
    // toggle was also a deliberate "varies per person".
    set.add('commissionToggle');
    if (initial.teamCommissionEnabled) set.add('commissionPercent');
    return set;
    // initial is stable per mount: the onboard page remounts on template swap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guardActive]);
  const warnedFields = React.useRef<Set<string>>(new Set());
  const confirmPublisherEdit = (field: string, message: string): boolean => {
    if (!publisherProvided.has(field) || warnedFields.current.has(field)) return true;
    const ok = window.confirm(message);
    if (ok) warnedFields.current.add(field);
    return ok;
  };
  const confirmField = (field: string, label: string): boolean =>
    confirmPublisherEdit(
      field,
      `The publisher entered ${label} on their onboarding form. Are you sure you want to change it?`,
    );

  const toggleDevice = (device: string) => {
    setDevices((prev) => {
      const next = new Set(prev);
      if (next.has(device)) next.delete(device);
      else next.add(device);
      return next;
    });
  };

  const patchIdentity = (patch: Partial<IdentityValues>) => {
    if (patch.slug !== undefined && patch.slug !== slug
      && !confirmField('slug', 'this web address')) return;
    if (patch.displayName !== undefined && patch.displayName !== displayName
      && !confirmField('displayName', 'this station name')) return;
    if (patch.slug !== undefined) setSlug(patch.slug);
    if (patch.displayName !== undefined) setDisplayName(patch.displayName);
    if (patch.website !== undefined) setWebsite(patch.website);
  };

  const brandingValues: BrandingValues = {
    logoUrl, logoInvert, logoScale, primaryColor, secondaryColor, accentColor, fontFamily, fontUrl,
  };
  const patchBranding = (patch: Partial<BrandingValues>) => {
    if (patch.logoUrl !== undefined && patch.logoUrl !== logoUrl
      && !confirmField('logoUrl', 'their logo')) return;
    if (patch.primaryColor !== undefined && patch.primaryColor !== primaryColor
      && !confirmField('primaryColor', 'their primary color')) return;
    if (patch.accentColor !== undefined && patch.accentColor !== accentColor
      && !confirmField('accentColor', 'their accent color')) return;
    // Any change to the logo invalidates the transparency verdict, which only
    // describes the logo a scrape returned.
    if (patch.logoUrl !== undefined) { setLogoUrl(patch.logoUrl); setLogoNotTransparent(false); }
    if (patch.logoInvert !== undefined) setLogoInvert(patch.logoInvert);
    if (patch.logoScale !== undefined) setLogoScale(patch.logoScale);
    if (patch.primaryColor !== undefined) setPrimaryColor(patch.primaryColor);
    if (patch.secondaryColor !== undefined) setSecondaryColor(patch.secondaryColor);
    if (patch.accentColor !== undefined) setAccentColor(patch.accentColor);
    if (patch.fontFamily !== undefined) setFontFamily(patch.fontFamily);
    if (patch.fontUrl !== undefined) setFontUrl(patch.fontUrl);
  };

  return (
    <form
      onSubmit={handleSubmit}
      onMouseDown={redirectTopPaddingClickToCaretEnd}
      className="space-y-5"
    >
      {successMessage && (
        <div className="flex items-start gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
          <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" />
          <p className="text-[13px] text-emerald-800 leading-relaxed">{successMessage}</p>
        </div>
      )}
      {error && (
        <div className="flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
          <XCircle className="w-4 h-4 mt-0.5 text-rose-600 shrink-0" />
          <p className="text-[13px] text-rose-800 leading-relaxed">{error}</p>
        </div>
      )}

      {sourceLabel && (
        <div className="flex items-start gap-2.5 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3">
          <Sparkles className="w-4 h-4 mt-0.5 text-sky-600 shrink-0" />
          <p className="text-[13px] text-sky-900 leading-relaxed">
            Pre-filled from <strong>{sourceLabel}</strong>&rsquo;s onboarding form. Sections
            marked <em>Internal only</em> are yours to set, and the publisher never saw them.
          </p>
        </div>
      )}

      <div id="pf-sec-identity" className="scroll-mt-6">
      <Section
        title="Identity"
        subtitle="The slug becomes the URL: /self-service/{slug}."
        icon={Building2}
        badge={sourceLabel ? <FromPublisherPill /> : undefined}
      >
        {isSuperAdminCreating && (
          <div className="mb-4">
            <label className={labelClass}>Organization</label>
            {organizationLocked ? (
              // Fixed by the onboarding link the publisher filled in. The API
              // pins it from the intake row and ignores the body, so offering a
              // picker here would only let the admin choose something that gets
              // silently overridden.
              <>
                <input
                  readOnly
                  value={organizations.find((o) => o.id === organizationId)?.name
                    ?? (organizationsLoading ? 'Loading…' : organizationId)}
                  className={readonlyInputClass}
                />
                <p className="mt-1 text-[11px] text-slate-500">
                  Set when this publisher&rsquo;s onboarding link was created.
                </p>
              </>
            ) : (
              <>
                <select
                  required
                  value={organizationId}
                  onChange={(e) => setOrganizationId(e.target.value)}
                  disabled={organizationsLoading}
                  className={inputClass}
                >
                  <option value="">
                    {organizationsLoading ? 'Loading organizations…' : 'Select organization…'}
                  </option>
                  {organizations.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-slate-500">
                  Which organization owns this channel. Required for super_admins because
                  their account isn't tied to a single org.
                </p>
              </>
            )}
          </div>
        )}
        <IdentityFields
          values={identityValues}
          onChange={patchIdentity}
          slugReadOnly={mode === 'edit'}
          extracting={extracting}
          onExtract={handleExtract}
        />
      </Section>
      </div>

      <div id="pf-sec-branding" className="scroll-mt-6">
      <Section
        title="Branding"
        subtitle="Logo, brand colors, and font that drive the channel's portal theme."
        icon={Palette}
        badge={sourceLabel ? <FromPublisherPill /> : undefined}
      >
        <BrandingFields
          values={brandingValues}
          onChange={patchBranding}
          logoNotTransparent={logoNotTransparent}
          extractingLogo={extractingLogo}
          onExtractLogo={handleExtractLogo}
          channelName={displayName.trim() || name.trim()}
        />
      </Section>
      </div>

      <div id="pf-sec-video" className="scroll-mt-6">
      <Section
        title="Landing background video"
        subtitle="Optional. Stitch 1-6 clips into the TV-hero video, plays clean for 3s, then blurs into a backdrop. Leave empty to keep the gradient (or the current video)."
        icon={Film}
        badge={sourceLabel ? <FromPublisherPill /> : undefined}
      >
        <BackgroundVideoBuilder
          currentVideoUrl={initial?.backgroundVideoUrl}
          slug={mode === 'edit' ? initial?.slug : null}
          clips={backgroundClips}
          onChange={setBackgroundClips}
        />
      </Section>
      </div>

      <div id="pf-sec-commercial" className="scroll-mt-6">
      <Section
        title="Commercial"
        subtitle="Pricing and policy defaults applied to every campaign on this channel. Advertisers see these as guardrails in the wizard."
        icon={DollarSign}
        badge={sourceLabel ? <InternalOnlyPill /> : undefined}
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className={labelClass}>Coverage</label>
            <select value={coverage} onChange={(e) => setCoverage(e.target.value as 'regional' | 'national')} className={inputClass}>
              <option value="regional">Regional</option>
              <option value="national">National</option>
            </select>
            <p className="mt-1 text-[11px] text-slate-500">
              Local channels get radius, state, and metro targeting; national channels get state-only targeting.
            </p>
          </div>
          <div>
            <label className={labelClass}>Rev share (%)</label>
            <input type="number" step="0.01" value={revSharePercent} onChange={(e) => setRevSharePercent(e.target.value)} className={inputClass} />
            {/* CTV-719 / ADR-0035: paid per delivery month, 60 days after
                that month ends. The old "60-90 days after the charge" predates
                the settlement engine and was wrong on both counts. */}
            <p className="mt-1 text-[11px] text-slate-500">
              CTVBuyer's share of media spend. The station's payout =
              campaign budget × (1 − rev share), paid one delivery month at a
              time, 60 days after each month ends.
            </p>
          </div>
          <div>
            <label className={labelClass}>Min budget ($)</label>
            <input type="number" min={250} value={minBudget} onChange={(e) => setMinBudget(e.target.value)} className={inputClass} />
            <p className="mt-1 text-[11px] text-slate-500">
              The smallest campaign the wizard will accept (pre-fee).
            </p>
          </div>
          <div>
            <label className={labelClass}>Min campaign days</label>
            <input type="number" value={minDays} onChange={(e) => setMinDays(e.target.value)} className={inputClass} />
            <p className="mt-1 text-[11px] text-slate-500">
              Shortest flight duration available in the date picker.
            </p>
          </div>
          <div className="sm:col-span-3">
            <label className={labelClass}>Support email <span className="font-normal normal-case tracking-normal text-slate-400">(optional)</span></label>
            <input type="email" value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} className={inputClass} />
            <p className="mt-1 text-[11px] text-slate-500">
              Surfaced in the wizard's footer so advertisers can reach the station directly.
            </p>
          </div>
          <div className="sm:col-span-2">
            <label className={labelClass}>Ad Ops / Technical contact email <span className="font-normal normal-case tracking-normal text-slate-400">(optional)</span></label>
            <input type="email" value={adopsEmail} onChange={(e) => { if (confirmField('adopsEmail', 'this contact email')) setAdopsEmail(e.target.value); }} className={inputClass} />
            <p className="mt-1 text-[11px] text-slate-500">
              Who we reach for trafficking, streams, and integrations. Never
              shown to advertisers. Loading an intake submission fills this in
              from what the publisher entered.
            </p>
          </div>
          <div>
            <label className={labelClass}>Ad Ops phone <span className="font-normal normal-case tracking-normal text-slate-400">(optional)</span></label>
            <input type="tel" value={adopsPhone} onChange={(e) => { if (confirmField('adopsPhone', 'this contact phone')) setAdopsPhone(e.target.value); }} className={inputClass} />
          </div>
          <div className="sm:col-span-3">
            <label className="inline-flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showCommission}
                onChange={(e) => {
                  if (
                    confirmPublisherEdit(
                      'commissionToggle',
                      'The publisher answered the commission question on their onboarding form. Are you sure you want to change their answer?',
                    )
                  ) {
                    setShowCommission(e.target.checked);
                  }
                }}
                className="mt-0.5 w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
              />
              <span className="text-[12px] text-slate-600">
                <span className="font-medium text-slate-900">Show team notional commission</span>
                {' '}on Referrals. Estimates only, never billing. Pre-checked
                when the publisher asked for it on their onboarding form.
              </span>
            </label>
            {showCommission && (
              <div className="mt-3 max-w-xs">
                <label className={labelClass}>Default commission % <span className="font-normal normal-case tracking-normal text-slate-400">(optional)</span></label>
                <input type="number" step="0.1" min={0} max={100} value={commissionPercent} onChange={(e) => { if (confirmField('commissionPercent', 'their commission preference')) setCommissionPercent(e.target.value); }} className={inputClass} />
                <p className="mt-1 text-[11px] text-slate-500">
                  Blank = rates vary by person; set them individually on Referrals.
                </p>
              </div>
            )}
          </div>
        </div>
      </Section>
      </div>

      <div id="pf-sec-devices" className="scroll-mt-6">
      <Section
        title="Devices"
        // CTV-855: admin-owned, never publisher-provided, so no pill and no
        // provenance claim in either mode.
        subtitle="Which devices this station or channel streams on."
        icon={MonitorSmartphone}
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
          {STREAMING_DEVICES.map(({ value, label }) => (
            <label
              key={value}
              className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
            >
              <input
                type="checkbox"
                checked={devices.has(value)}
                onChange={() => toggleDevice(value)}
                className="w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
              />
              <span className="text-[13px] text-slate-700">{label}</span>
            </label>
          ))}
        </div>
      </Section>
      </div>

      {/* CTV-752: hide the whole Materials tab while a station's collateral
          isn't ready to hand out. */}
      <div id="pf-sec-materials" className="scroll-mt-6">
      <Section
        title="Materials"
        subtitle="Print-ready collateral the station team can hand to advertisers."
        icon={FileText}
        badge={sourceLabel ? <InternalOnlyPill /> : undefined}
      >
        <label className="inline-flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={materialsEnabled}
            onChange={(e) => setMaterialsEnabled(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
          />
          <span className="text-[12px] text-slate-600">
            <span className="font-medium text-slate-900">Show the Materials tab</span>
            {' '}in this station's publisher admin page. Uncheck to hide from
            the station team until it's ready.
          </span>
        </label>
      </Section>
      </div>

      <div id="pf-sec-markets" className="scroll-mt-6">
      <Section
        title="Metros"
        subtitle="Where this channel reaches viewers."
        icon={MapPin}
        badge={sourceLabel ? <FromPublisherPill /> : undefined}
      >
        <div className="space-y-4">
          {markets.map((m, i) => (
            <div key={i} className="space-y-2 border border-slate-200 rounded-xl p-3">
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-12 sm:col-span-6">
                  <label className={labelClass}>Metro</label>
                  <MetroCombobox
                    value={m.dmaCode}
                    onSelect={(dma) => setMarkets((arr) => selectDmaForRow(arr, i, dma))}
                    disabledCodes={markets
                      .filter((_, idx) => idx !== i)
                      .map((other) => other.dmaCode)
                      .filter(Boolean)}
                  />
                </div>
                <div className="col-span-10 sm:col-span-5">
                  <label className={labelClass}>Channels (comma-separated)</label>
                  <input value={m.channels} onChange={(e) => updateMarket(i, { channels: e.target.value })} placeholder="WISC-TV" className={inputClass} />
                </div>
                <div className="col-span-2 sm:col-span-1 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setMarkets((arr) => removeMarketRow(arr, i))}
                    className="p-2.5 rounded-xl text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                    aria-label="Remove market"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setMarkets(appendBlankMarketRow)}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-700 hover:text-slate-900 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add market
          </button>

          {/* One channel-wide list, not one per market. Nothing reads
              coveredStates per-DMA: the wizard's suggested-state chips flatten
              it across markets and the center label takes markets[0][0], so a
              per-market editor was busywork that could only ever produce the
              same two outcomes. Still written to every market to keep the
              stored shape unchanged. */}
          {markets.length > 0 && (
            <div className="border-t border-slate-200/70 pt-4">
              <label className={labelClass}>Covered states</label>
              <StateSelector
                selectedStates={coveredStates}
                onChange={(next) => setMarkets((arr) => applyStatesToAll(arr, next))}
                accentColor={accentColor}
                showMap={false}
              />
              <p className="mt-1 text-[11px] text-slate-500">
                Surfaced in the wizard as "Suggested states" when an advertiser narrows their targeting. Add border states a station reaches (e.g., KEZI in Eugene also reaches Northern California, so <code>OR, CA</code>).
              </p>
            </div>
          )}
        </div>
      </Section>
      </div>

      <div id="pf-sec-ron" className="scroll-mt-6">
      <Section
        title="Products"
        subtitle="Every campaign on this channel runs as Run-of-Network across the Publica sub-channels selected below. Adjust the CPM and uncheck any sub-channels you don't want to include."
        icon={Tv}
        badge={sourceLabel ? <InternalOnlyPill /> : undefined}
      >
        <div className="space-y-4">
          {/* CTV-745: the internal name lives with the Publica wiring it
              drives. Staff-owned, deliberately never pre-filled from a
              publisher's onboarding form. */}
          <InternalNameField
            value={name}
            onChange={setName}
            channels={orgChannels}
            loading={orgChannelsLoading}
          />
          {subChannelsWarning && (
            <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
              <XCircle className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
              <p className="text-[12px] text-amber-800 leading-relaxed">{subChannelsWarning}</p>
            </div>
          )}
          <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 space-y-4">
            <div className="grid grid-cols-12 gap-3 items-end">
              <div className="col-span-12 sm:col-span-4">
                <label className={labelClass}>Product</label>
                <input value="Run of Network" readOnly className={readonlyInputClass} />
              </div>
              <div className="col-span-12 sm:col-span-3">
                <label className={labelClass}>CPM ($)</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={ronCpm}
                  onChange={(e) => { if (confirmField('ronCpm', 'their CPM')) setRonCpm(e.target.value); }}
                  className={inputClass}
                />
              </div>
              <div className="col-span-12 sm:col-span-5 flex sm:justify-end items-end">
                {availableSubChannels.length > 0 && (
                  <button
                    type="button"
                    onClick={() =>
                      setIncludedSubChannels(
                        allChecked ? new Set() : new Set(availableSubChannels),
                      )
                    }
                    className="text-[12px] font-semibold text-slate-700 hover:text-slate-900 underline underline-offset-2 decoration-1"
                  >
                    {allChecked ? 'Uncheck all' : 'Check all'}
                  </button>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className={labelClass}>
                  Sub-channels
                  {availableSubChannels.length > 0 && (
                    <span className="ml-2 font-normal normal-case tracking-normal text-slate-400">
                      ({includedSubChannels.size}/{availableSubChannels.length} included)
                    </span>
                  )}
                </label>
                {subChannelsLoading && (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />
                )}
              </div>
              {availableSubChannels.length === 0 ? (
                <p className="text-[12px] text-slate-500 leading-relaxed px-1">
                  {organizationId && name.trim()
                    ? 'No matching Publica sub-channels found yet. Adjust the Publica name to match a channel prefix.'
                    : 'Pick an organization and enter the Publica name above to load available sub-channels.'}
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {availableSubChannels.map((channel) => (
                    <label
                      key={channel}
                      className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-white cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={includedSubChannels.has(channel)}
                        onChange={() => toggleSubChannel(channel)}
                        className="w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                      />
                      <span className="text-[13px] text-slate-700 truncate">{channel}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </Section>
      </div>

      <div className="flex items-center justify-end gap-3 pt-2">
        {/* The detail banners live at the top of a long form, out of view
            from the save button; mirror the outcome inline so the click gets
            visible feedback where it happened. */}
        {successMessage && !submitting && (
          <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-emerald-700">
            <CheckCircle2 className="w-4 h-4" />
            Saved
          </span>
        )}
        {error && !submitting && (
          <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-rose-700">
            <XCircle className="w-4 h-4" />
            Save failed (details above)
          </span>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-[13px] font-semibold text-white bg-slate-900 hover:bg-slate-700 disabled:opacity-50 transition-colors"
        >
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {buildingVideo
            ? 'Rendering video…'
            : submitting
              ? 'Saving…'
              : (submitLabel ?? (mode === 'create' ? 'Create publisher' : 'Save changes'))}
        </button>
      </div>
    </form>
  );
};

const FromPublisherPill = () => (
  <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-sky-700">
    From publisher
  </span>
);

const InternalOnlyPill = () => (
  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-amber-700">
    Internal only
  </span>
);

export default PublisherForm;
